import { useEffect, useId, useRef, useState } from 'react';
import { useLoaderData } from 'react-router';

import { cn } from '@rocco/utils/cn';
import type { loader } from '@modules/professional/page/loaders/overview-page-loader.server';
import { BookMeetingRowContent } from '~/modules-rocco/booking';
import {
    NumberedAccordion,
    RICH_TEXT_HTML_CLASS,
    detailCta,
    type NumberedAccordionRow,
} from '~/ui-rocco/pages/detail-shared';
import { canBookDirectMeeting } from '../utils/book-meeting';

const TESTIMONIALS_INITIAL = 4;
const TESTIMONIALS_STEP = 4;
const TESTIMONIAL_CLAMP_LINES = 5;
// Collapsed quote height: 5 lines at line-height 1.5. em-based so it tracks the
// responsive font size without a measurement flash. The card's own min-height
// keeps short testimonials from rendering as tiny cards.
const TESTIMONIAL_CLAMP_EM = '7.5em';

type AboutLoaderData = ReturnType<typeof useLoaderData<typeof loader>>;
type Testimonial = NonNullable<
    AboutLoaderData['professionalOverview']['professionalTestimonials']
>[number];

const quoteGlyph =
    'font-sans leading-[0.5] text-[#d2d2d2] max-md:text-[2.5rem] md:text-[3.5rem]';

/** Filled-out-of-five star rating; hidden when there's no rating. */
const StarRating = ({ rating }: { rating: number }) => {
    const rounded = Math.round(rating);
    if (rounded <= 0) return null;
    return (
        <div
            className="flex items-center gap-1"
            aria-label={`${rounded} out of 5`}
        >
            {Array.from({ length: 5 }).map((_, index) => (
                <svg
                    key={index}
                    viewBox="0 0 20 20"
                    aria-hidden="true"
                    className={cn(
                        'size-[0.95rem]',
                        index < rounded ? 'text-[#181818]' : 'text-[#d6d6d6]',
                    )}
                    fill="currentColor"
                >
                    <path d="M10 1.5l2.6 5.27 5.82.85-4.21 4.1.99 5.78L10 14.77l-5.2 2.73.99-5.78L1.58 7.62l5.82-.85L10 1.5z" />
                </svg>
            ))}
        </div>
    );
};

/**
 * One testimonial as a quote card. The quote is clamped to keep cards compact;
 * when it overflows, a Read more button reveals the rest in place. Only this
 * card grows — the grid is top-aligned so a neighbour in the same row keeps its
 * height.
 *
 * The control is a real button rather than a click handler on the card: the
 * card is a `figure`, so a handler there would leave the hidden text
 * unreachable by keyboard and give no indication it could be opened at all.
 */
const TestimonialCard = ({ testimonial }: { testimonial: Testimonial }) => {
    const contentRef = useRef<HTMLDivElement>(null);
    const [expanded, setExpanded] = useState(false);
    const [clampPx, setClampPx] = useState(0);
    const [fullPx, setFullPx] = useState(0);
    const quoteId = useId();

    useEffect(() => {
        const el = contentRef.current;
        if (!el) return;
        const measure = () => {
            const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || 0;
            setClampPx(lineHeight * TESTIMONIAL_CLAMP_LINES);
            setFullPx(el.scrollHeight);
        };
        measure();
        document.fonts?.ready.then(measure).catch(() => {});
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const truncated = fullPx > clampPx + 1;

    return (
        // The min-height stops a one-line testimonial rendering as a stub, but
        // at 18rem it overshot the shortest cards by around 100px, and centring
        // split that into empty bands above and below the text — which the
        // corner quote marks then framed instead of the quote. 14rem still
        // reads as a card while sitting much closer to the content.
        <figure className="relative m-0 flex h-fit min-h-[12rem] flex-col justify-center rounded-[0.5rem] bg-[#f6f6f6] md:min-h-[14rem]">
            {/* Corner quotes frame the centred content. The marks hang high in
                their box, so the top is nudged down to mirror the bottom's gap. */}
            <span
                aria-hidden="true"
                className={cn(
                    quoteGlyph,
                    'absolute left-[1.75rem] max-md:top-[2.1rem] md:top-[3rem]',
                )}
            >
                &ldquo;
            </span>
            <span
                aria-hidden="true"
                className={cn(
                    quoteGlyph,
                    'absolute right-[1.75rem] bottom-[1.5rem]',
                )}
            >
                &rdquo;
            </span>
            <div className="flex flex-col gap-[1rem] max-md:px-8 max-md:py-10 md:px-[3.5rem] md:py-[3.75rem]">
                {testimonial.Rating ? (
                    <StarRating rating={Number(testimonial.Rating)} />
                ) : null}
                {/* Clamp the collapsed quote to 5 lines; the max-height
                    transition animates the reveal. */}
                <div
                    id={quoteId}
                    ref={contentRef}
                    style={{
                        maxHeight: expanded
                            ? fullPx
                                ? fullPx
                                : undefined
                            : TESTIMONIAL_CLAMP_EM,
                    }}
                    className={cn(
                        'overflow-hidden font-sans leading-[1.5] font-normal text-[#1f1f21] transition-[max-height] duration-[360ms] ease-out motion-reduce:transition-none max-md:text-base md:text-[1.248rem]',
                        RICH_TEXT_HTML_CLASS,
                    )}
                    dangerouslySetInnerHTML={{
                        __html: testimonial.Content ?? '',
                    }}
                />
                {truncated ? (
                    <button
                        type="button"
                        onClick={() => setExpanded((value) => !value)}
                        aria-expanded={expanded}
                        aria-controls={quoteId}
                        className="self-start font-sans font-medium text-[#181818] underline underline-offset-4 transition-opacity hover:opacity-70 max-md:text-sm md:text-base"
                    >
                        {expanded ? 'Read less' : 'Read more'}
                    </button>
                ) : null}
                {testimonial.Source || testimonial.Location ? (
                    <figcaption className="font-sans leading-tight max-md:text-sm md:text-base">
                        {testimonial.Source ? (
                            // The CMS stores Source as rich text, so it arrives
                            // wrapped in a block element. Left as-is that block
                            // breaks the line and strands the separator below at
                            // the start of the next one, so collapse its
                            // children inline.
                            <span
                                className="font-medium text-[#141414] [&_p]:m-0 [&_p]:inline"
                                dangerouslySetInnerHTML={{
                                    __html: testimonial.Source,
                                }}
                            />
                        ) : null}
                        {testimonial.Location ? (
                            // #767676 on this card's #f6f6f6 is 4.2:1, under the
                            // 4.5:1 AA floor for text this size. #6e6e6e clears
                            // it at 4.72:1 while staying visibly secondary.
                            <span className="text-[#6e6e6e]">
                                {testimonial.Source ? ' · ' : ''}
                                {testimonial.Location}
                            </span>
                        ) : null}
                    </figcaption>
                ) : null}
            </div>
        </figure>
    );
};

/** (02) What our Clients say — the testimonials grid + Load More. */
const TestimonialsRow = ({ testimonials }: { testimonials: Testimonial[] }) => {
    const [visible, setVisible] = useState(TESTIMONIALS_INITIAL);
    const shown = testimonials.slice(0, visible);

    return (
        <div className="pt-[0.75rem] pb-[1.8rem] max-md:pb-4">
            {/* Top-aligned so expanding one card never stretches its row neighbour. */}
            <div className="grid items-start gap-[1.25rem] md:grid-cols-2">
                {shown.map((testimonial) => (
                    <TestimonialCard
                        key={testimonial.ID}
                        testimonial={testimonial}
                    />
                ))}
            </div>
            {/* Load More appends cards in place and keeps focus on the button,
                so without this a screen reader hears nothing and cannot tell
                the press did anything. Rendered whether or not more remain, so
                the region is already in the tree when the count changes. */}
            <div aria-live="polite" className="sr-only">
                {`Showing ${shown.length} of ${testimonials.length} testimonials`}
            </div>
            {visible < testimonials.length ? (
                <div className="flex justify-center pt-[2.5rem]">
                    <button
                        type="button"
                        onClick={() =>
                            setVisible((value) => value + TESTIMONIALS_STEP)
                        }
                        className={detailCta('filled-light')}
                    >
                        Load More
                    </button>
                </div>
            ) : null}
        </div>
    );
};

/**
 * Overview accordion (Figma 2354:58614) — "Book a Meeting" and "What our
 * Clients say". Book a Meeting only shows when the professional has a bookable
 * (active, one-on-one) Client Director session; otherwise it's hidden. The
 * testimonials row shows the professional's testimonials as clamped quote cards
 * (Load More for the rest) and self-hides when there are none. The whole
 * accordion drops out when neither row applies.
 */
export const ProfessionalOverviewAccordionSection = () => {
    const { professionalOverview } = useLoaderData<typeof loader>();

    const testimonials = [
        ...(professionalOverview.professionalTestimonials ?? []),
    ]
        .filter((testimonial) => testimonial.Content)
        .sort((a, b) => (a.MemberSortOrder ?? 0) - (b.MemberSortOrder ?? 0));

    const session = professionalOverview.ClientDirectorCalendarSession;
    const rows: NumberedAccordionRow[] = [];

    if (canBookDirectMeeting(session)) {
        rows.push({
            title: 'Book a Meeting',
            content: (
                <BookMeetingRowContent
                    sessions={[{ ID: session.ID }]}
                    theme="light"
                />
            ),
        });
    }

    if (testimonials.length > 0) {
        rows.push({
            title: 'What our Clients say',
            content: <TestimonialsRow testimonials={testimonials} />,
        });
    }

    return (
        <NumberedAccordion
            headingLevel={2}
            theme="light"
            multiple
            rows={rows}
            triggerClassName="md:[&>svg]:size-8"
        />
    );
};
